import { ApiResponse } from '../api'
import { axios } from '@src/lib/axios'
import { queryStrProcess, fileSave } from '@src/utils'
import _ from 'lodash'

export type Category = 'car-records'|'car-infos'|'pio-records'|'employee-records'|'car-statistics'|'total-output';

const CONDITION_BY_CATEGORY = {
  CAR_RECORD: ['fromDate', 'fromTime', 'toDate', 'toTime', 'carModelUuid', 'engineNo', 'sfxNo', 'stationUuid', 'employee', 'shiftUuid', 'status', 'page', 'pageSize'],
  CAR_INFO: ['status', 'carModelUuid', 'engineNo', 'sfxNo', 'shippingNo', 'page', 'pageSize'],
  PIO_RECORD: ['pioItemUuid', 'fromDate', 'fromTime', 'toDate', 'toTime', 'engineNo', 'idotNo', 'keyword', 'page', 'pageSize'],
  EMPLOYEE_RECORD: ['fromDate', 'fromTime', 'toDate', 'toTime', 'employeeNo', 'name', 'carModelUuid', 'sfxNo', 'page', 'pageSize'],
  CAR_STATISTICS: ['fromDate', 'fromTime', 'toDate', 'toTime', 'engineNo', 'carModelUuid', 'sfxNo', 'stationUuid', 'page', 'pageSize'],
  TOTAL_OUTPUT: ['fromDate', 'fromTime', 'toDate', 'toTime', 'page', 'pageSize']
}

export interface GetReportReqDto {
  searchCondition: { [name: string]: string };
}

export interface CarRecordDto {}

export interface CarRecordsRespDto {
  totalNumber: number;
  data: CarRecordDto[] | CarStatisticsDto;
  totalPages: number;
  argFieldNames?: string[];
}

/** 取得車輛組裝紀錄 */
export async function getCarRecords(reqDto: GetReportReqDto): Promise<CarRecordsRespDto> {
  const { searchCondition } = reqDto
  
  const factoryUuid = searchCondition['factoryUuid']
  const category = searchCondition['category']
  const conditionFieldName = CONDITION_BY_CATEGORY.CAR_RECORD
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/car-records/${factoryUuid}/${category}?${queryStr}`
  const response = await axios.get<ApiResponse<CarRecordsRespDto>>(apiPath)

  return response.data
}

export async function exportCarRecords(reqDto: GetReportReqDto): Promise<void> {
  const { searchCondition } = reqDto
  const factoryUuid = searchCondition['factoryUuid']
  const category = searchCondition['category']
  const conditionFieldName = CONDITION_BY_CATEGORY.CAR_RECORD
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/car-records/${factoryUuid}/${category}/export?${queryStr}`

  const response = await axios.get<ApiResponse<Blob>>(apiPath, { responseType: 'blob' })
  fileSave(response.data, '車輛紀錄.xlsx')
}

export interface GetCarRecordDetailReqDto {
  searchCondition: { [name: string]: string };
  engineNo: string;
  stationUuid: string;
}

export interface CarRecordDetailDto {
  employeeName: string;
  action?: string;
  status: string;
  startTime: string;
  partName?: string;
  endTime: string;
  duration: string;
}

export interface GetCarRecordDetailRespDto {
  engineNo: string;
  sfxNo: string;
  factoryDescription: string;
  stationDescription?: string;
  records: CarRecordDetailDto[];
}

/** 取得車輛紀錄明細 */
export async function getCarRecordDetail(reqDto: GetCarRecordDetailReqDto): Promise<GetCarRecordDetailRespDto> {
  const { engineNo, stationUuid, searchCondition } = reqDto
  
  const category = searchCondition['category']

  const apiPath = `/api/web/report/car-records/engineNo/${engineNo}/${category}?stationUuid=${stationUuid}`
  const response = await axios.get<ApiResponse<GetCarRecordDetailRespDto>>(apiPath)

  return response.data
}

export interface CarInfoDto {
  engineNo: string;
  sfxNo: string;
  carModelCode: string;
  shippingNo: string;
  status: string;
  createdAt: string;
}

export interface CarInfosRespDto {
  totalNumber: number;
  data: CarInfoDto[];
  totalPages: number;
  argFieldNames?: string[];
}

/** 車籍資料 CarInfo */
export async function getCarInfos(reqDto: GetReportReqDto): Promise<CarInfosRespDto> {
  const { searchCondition } = reqDto
  
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.CAR_INFO
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/car-infos/${factoryUuid}?${queryStr}`
  const response = await axios.get<ApiResponse<CarInfosRespDto>>(apiPath)

  const newData = _.map(response.data.data, o => ({...o, carStatus: o.status}))
  return {
    ...response.data,
    data: newData
  }
}

export async function exportCarInfos(reqDto: GetReportReqDto): Promise<void> {
  const { searchCondition } = reqDto
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.CAR_INFO
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/car-infos/${factoryUuid}/export?${queryStr}`

  const response = await axios.get<ApiResponse<Blob>>(apiPath, { responseType: 'blob' })
  fileSave(response.data, '車籍資料.xlsx')
}

export interface GetCarInfoReqDto {
  engineNo: string;
}

export interface GetCarInfoRespDto {
  engineNo: string;
  idotNo: string;
}

/** 取得單筆車籍資料 */
export async function getCarInfo(reqDto: GetCarInfoReqDto): Promise<GetCarInfoRespDto> {
  const { engineNo } = reqDto

  const apiPath = `/api/web/report/car-infos/engineNo/${engineNo}`
  const response = await axios.get<ApiResponse<GetCarInfoRespDto>>(apiPath)

  return response.data
}

export interface UpdateCarInfoReqDto {
  engineNo: string;
  data: {
    engineNo: string;
    idotNo: string;
  };
}

/** 更新車籍資料 */
export async function updateCarInfo(reqDto: UpdateCarInfoReqDto): Promise<void> {
  const { engineNo, data } = reqDto

  await axios.put<ApiResponse<void>>(`/api/web/report/car-infos/engineNo/${engineNo}`, data)
}

export interface DeleteCarInfoReqDto {
  engineNo: string;
}

/** 刪除車籍資料 */
export async function deleteCarInfo(reqDto: DeleteCarInfoReqDto): Promise<void> {
  const { engineNo } = reqDto

  await axios.delete<ApiResponse<void>>(`/api/web/report/car-infos/engineNo/${engineNo}`)
}

export interface PIORecordDto {
  assemblyCompletedAt: string;
  assemblyPioArgs: Array<{fieldName: string, value: string}>;
  engineNo: string;
  idotNo: string;
  qrCode: string;
}

export interface PIORecordsRespDto {
  totalNumber: number;
  data: PIORecordDto[];
  totalPages: number;
  argFieldNames?: string[];
  assemblyPioArgs?: Array<{[fieldName: string]: string}>
}

/** 項目(PIO)報表 */
export async function getPIORecords(reqDto: GetReportReqDto): Promise<PIORecordsRespDto> {
  const { searchCondition } = reqDto
  
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.PIO_RECORD
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/pio-records/${factoryUuid}?${queryStr}`
  const response = await axios.get<ApiResponse<PIORecordsRespDto>>(apiPath)

  const newData = _.map(response.data.data, o => ({...o, ..._(o.assemblyPioArgs).keyBy('fieldName').mapValues(v => v.value).value()}))
  return {
    ...response.data,
    data: newData
  }
}

export async function exportPIORecords(reqDto: GetReportReqDto): Promise<void> {
  const { searchCondition } = reqDto
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.PIO_RECORD
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/pio-records/${factoryUuid}/export?${queryStr}`

  const response = await axios.get<ApiResponse<Blob>>(apiPath, { responseType: 'blob' })
  fileSave(response.data, '項目紀錄.xlsx')
}

export interface GetPIOItemArgsReqDto {
  engineNo: string;
  pioItemUuid: string;
}

export interface ArgItem {
  uuid: string;
  fieldName: string;
  value: string;
}

export interface GetPIOItemArgsRespDto {
  qrCode: string;
  idotNo: string;
  assemblyPioArgs: ArgItem[];
}

/** 取得PIO項目參數 */
export async function getPIOItemArgs(reqDto: GetPIOItemArgsReqDto): Promise<GetPIOItemArgsRespDto> {
  const { engineNo, pioItemUuid } = reqDto

  const response = await axios.get<ApiResponse<GetPIOItemArgsRespDto>>(`/api/web/report/pio-records/args/${pioItemUuid}/${engineNo}`)

  return response.data
}

export interface UpdatePIOItemArgsReqDto {
  engineNo: string;
  pioItemUuid: string;
  data: {
    qrCode: string;
    idotNo: string;
    assemblyPioArgs: ArgItem[];
  };
}

/** 更新PIO項目參數 */
export async function updatePIOItemArgs(reqDto: UpdatePIOItemArgsReqDto) {
  const { engineNo, pioItemUuid, data } = reqDto

  await axios.put<ApiResponse<void>>(`/api/web/report/pio-records/args/${pioItemUuid}/${engineNo}`, data)

}

export interface EmployeeRecordDto {
  /** 員工姓名 */
  employeeName: string;
  /** 員工編號 */
  employeeNo: string;
  /** 組裝數量 */
  recordCount: number;
  /** 組裝部位 */
  carPartDescription: string;
  /** 車種 */
  modelCode: string;
  /** sfx */
  sfxNo: string;
}

export interface EmployeeRecordsRespDto {
  totalNumber: number;
  data: EmployeeRecordDto[];
  totalPages: number;
  argFieldNames?: string[];
}

/** 員工紀錄 Employee record */
export async function getEmployeeRecords(reqDto: GetReportReqDto): Promise<EmployeeRecordsRespDto> {
  const { searchCondition } = reqDto
  
  const factoryUuid = searchCondition['factoryUuid']
  const category = searchCondition['category']
  const conditionFieldName = CONDITION_BY_CATEGORY.EMPLOYEE_RECORD
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/employee-records/${factoryUuid}/${category}?${queryStr}`
  const response = await axios.get<ApiResponse<EmployeeRecordsRespDto>>(apiPath)

  return response.data
}

export async function exportEmployeeRecords(reqDto: GetReportReqDto): Promise<void> {
  const { searchCondition } = reqDto
  const factoryUuid = searchCondition['factoryUuid']
  const category = searchCondition['category']
  const conditionFieldName = CONDITION_BY_CATEGORY.EMPLOYEE_RECORD
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/employee-records/${factoryUuid}/${category}/export?${queryStr}`

  const response = await axios.get<ApiResponse<Blob>>(apiPath, { responseType: 'blob' })
  fileSave(response.data, '員工紀錄.xlsx')
}

export interface CarStatisticsDto {
  moving: {
    movingInCount: string;
    movingOutCount: string;
    movingAvgTimeRange: string;
    movingMinTimeRange: string;
    movingMaxTimeRange: string;
  };
  assembly: {
    assemblyCount: string;
    assemblyAvgTimeRange: string;
    assemblyMinTimeRange: string;
    assemblyMaxTimeRange: string;
  };
  quality: {
    qualityCount: string;
    qualityAvgTimeRange: string;
    qualityMinTimeRange: string;
    qualityMaxTimeRange: string;
  };
  repair: {
    repairCount: string;
    repairAvgTimeRange: string;
    repairMinTimeRange: string;
    repairMaxTimeRange: string;
  };
}

export interface GetCarStatisticsRespDto {
  totalNumber: number;
  data: CarStatisticsDto;
  totalPages: number;
  argFieldNames?: string[];
}

/** 車輛整體統計 Car Statistics */
export async function getCarStatistics(reqDto: GetReportReqDto): Promise<GetCarStatisticsRespDto> {
  const { searchCondition } = reqDto
  
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.CAR_STATISTICS
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/car-statistics/${factoryUuid}?${queryStr}`
  const response = await axios.get<ApiResponse<GetCarStatisticsRespDto>>(apiPath)

  return response.data
}


export async function exportCarStatistics(reqDto: GetReportReqDto): Promise<void> {
  const { searchCondition } = reqDto
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.CAR_STATISTICS
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/car-statistics/${factoryUuid}/export?${queryStr}`

  const response = await axios.get<ApiResponse<Blob>>(apiPath, { responseType: 'blob' })
  fileSave(response.data, '車輛整體統計.xlsx')
}

export interface TotalOutputDto {
  carModelCode: string;
  sfxNo: string;
  shippingNo: string;
  recordCount: number;
}

export interface GetTotalOutputRespDto {
  totalNumber: number;
  data: TotalOutputDto[];
  totalPages: number;
  argFieldNames?: string[];
}

/** 總產量 Total Output */
export async function getTotalOutput(reqDto: GetReportReqDto): Promise<GetTotalOutputRespDto> {
  const { searchCondition } = reqDto
  
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.TOTAL_OUTPUT
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/total-output/${factoryUuid}?${queryStr}`
  const response = await axios.get<ApiResponse<GetTotalOutputRespDto>>(apiPath)

  return response.data
}

export async function exportTotalOutput(reqDto: GetReportReqDto): Promise<void> {
  const { searchCondition } = reqDto
  const factoryUuid = searchCondition['factoryUuid']
  const conditionFieldName = CONDITION_BY_CATEGORY.TOTAL_OUTPUT
  const queryStr = queryStrProcess(searchCondition, conditionFieldName)

  const apiPath = `/api/web/report/total-output/${factoryUuid}/export?${queryStr}`

  const response = await axios.get<ApiResponse<Blob>>(apiPath, { responseType: 'blob' })
  fileSave(response.data, '總產量.xlsx')
}